import braintree from 'braintree-web'
import axios from 'axios'
import { Country, State } from 'country-state-city'

async function getClientToken(api) {
	// takes this.store.api from the frontend as an argument
	console.log('Fetching the client token for braintree...')
	try {
		const response = await axios.get(`${api}/controllers/braintree/clientToken`)
		if (!response.data.success) {
			return { success: false, message: response.data.message }
		}
		return { success: true, token: response.data.token }
	} catch (error) {
		console.error('Error getting client token:', error)
		return { success: false, message: 'Error getting client token' }
	}
}

async function getSandboxClientToken(api) {
	// takes this.store.api from the frontend as an argument
	console.log('Fetching the client token for braintree...')
	try {
		const response = await axios.get(`${api}/controllers/braintree/sandbox/clientToken`)
		if (!response.data.success) {
			return { success: false, message: response.data.message }
		}
		return { success: true, token: response.data.token }
	} catch (error) {
		console.error('Error getting client token:', error)
		return { success: false, message: 'Error getting client token' }
	}
}

async function initiatePayPalCheckout(data) {
	const { clientToken, amount, shippingAddress, displayName, offerCredit } = data
	try {
		const clientInstance = await braintree.client.create({
			authorization: clientToken,
		})

		const paypalInstance = await braintree.paypal.create({ client: clientInstance })
		const payload = await paypalInstance.tokenize({
			flow: 'checkout',
			amount: amount,
			currency: 'USD',
			intent: 'sale',
			useraction: 'commit',
			localeCode: 'en_US',
			displayName: displayName,
			offerCredit,
		})

		if (shippingAddress) {
			payload.shippingAddressOverride = shippingAddress
			payload.shippingAddressEditable = false
		}

		return {
			success: true,
			nonce: payload.nonce,
			shippingAddress: payload.details?.shippingAddress,
			email: payload.details?.payerInfo?.email,
		}
	} catch (error) {
		console.error('Error initializing PayPal checkout:', error)
		return { success: false, error }
	}
}

async function initiateVenmoCheckout(data) {
	const { clientToken, amount } = data
	let venmoInstance

	try {
		console.log('Initializing braintree client instance...')
		const clientInstance = await braintree.client.create({
			authorization: clientToken,
		})

		console.log('Initializing braintree venmo instance...')
		venmoInstance = await braintree.venmo.create({
			client: clientInstance,
			// allowDesktopWebLogin: true,
			allowDesktop: true,
			paymentMethodUsage: 'single_use',
			totalAmount: amount,
		})

		if (!venmoInstance.isBrowserSupported()) {
			console.log('Venmo is not supported in this browser.')
			return { success: false, message: 'Venmo is not supported in this browser.' }
		}

		console.log('Tokenizing Venmo payment...')
		const payload = await venmoInstance.tokenize()
		console.log('Venmo payment tokenized. Nonce:', payload.nonce)
		return { success: true, nonce: payload.nonce }
	} catch (error) {
		console.error('Error initializing Venmo checkout:', error)
		return { success: false, error }
	} finally {
		if (venmoInstance) {
			venmoInstance.teardown()
		}
	}
}

async function initiateApplePayCheckout(data) {
	const { clientToken, amount, shippingAddress, displayName } = data
	try {
		const clientInstance = await braintree.client.create({
			authorization: clientToken,
		})

		const applePayInstance = await braintree.applePay.create({ client: clientInstance })
		const payload = await applePayInstance.tokenize({
			amount: amount,
			currencyCode: 'USD',
			shippingContact: shippingAddress,
			requiredBillingContactFields: ['postalAddress'],
			requiredShippingContactFields: ['postalAddress'],
		})

		return {
			success: true,
			nonce: payload.nonce,
			shippingAddress: payload.details?.shippingAddress,
			email: payload.details?.payerInfo?.email,
		}
	} catch (error) {
		console.error('Error initializing Apple Pay checkout:', error)
		return { success: false, error }
	}
}

async function dataCollector(clientInstance) {
	console.log('Initiate Braintree data collector instance')
	const dataCollectorInstance = await braintree.dataCollector.create({
		client: clientInstance,
	})

	console.log('Collecting device data...')
	return dataCollectorInstance.deviceData
}

async function initiateACHCheckout(data) {
	const { clientToken, billingAddress, newAccount, mandateText } = data

	try {
		console.log('Initiate ACH client instance')
		const clientInstance = await braintree.client.create({
			authorization: clientToken,
		})

		console.log('Initiate ACH data collector instance')
		const dataCollectorInstance = await braintree.dataCollector.create({
			client: clientInstance,
		})

		console.log('Collecting device data...')
		const deviceData = dataCollectorInstance.deviceData

		console.log('Initiate ACH usBankAccount instance')
		const usBankAccountInstance = await braintree.usBankAccount.create({
			client: clientInstance,
		})

		console.log('Collecting bank details...')
		const allStates = State.getStatesOfCountry('US')
		const state = allStates.find((state) => state.name === billingAddress.stateOrProvince)
		const stateCode = state ? state.isoCode : null

		const bankDetails = {
			accountNumber: newAccount.accountNumber,
			routingNumber: newAccount.routingNumber,
			accountType: newAccount.accountType,
			ownershipType: newAccount.ownershipType,
			billingAddress: {
				streetAddress: billingAddress.address1,
				extendedAddress: billingAddress.address2,
				locality: billingAddress.city,
				region: stateCode,
				postalCode: billingAddress.postalCode,
			},
		}

		if (bankDetails.ownershipType === 'personal') {
			bankDetails.firstName = newAccount.firstName
			bankDetails.lastName = newAccount.lastName
		} else {
			bankDetails.businessName = newAccount.businessName
		}

		const payload = await usBankAccountInstance.tokenize({
			bankDetails: bankDetails,
			mandateText,
			// 'By clicking "Continue", I authorize Braintree, a service of PayPal, on behalf of In His Hands Birth Supply (i) to verify my bank account information using bank information and consumer reports and (ii) to debit my bank account.',
		})

		return { success: true, nonce: payload.nonce, deviceData, billingAddress: bankDetails.billingAddress }
	} catch (error) {
		console.error('Error initializing ACH checkout:', error)
		return { success: false, error }
	}
}

async function processPayment(data) {
	const { api, nonce, amount, bcOrderId, deviceData } = data
	try {
		const paymentData = {
			paymentMethodNonce: nonce,
			amount: amount,
			bcOrderId,
		}
		if (deviceData) paymentData.deviceData = deviceData

		const response = await axios.post(`${api}/controllers/braintree/processPayment`, paymentData)

		if (response.data.success) {
			console.log('Payment successful! Braintree Transaction ID:', response.data.data.id)
			return response.data
		} else {
			console.error('Payment failed:', response.data.error)
		}
	} catch (error) {
		console.error('Error processing payment:', error)
		throw error
	}
}

async function processACHPayment(data) {
	const { accountHolder, nonce, token, amount, deviceData, bcOrderId, api, billingAddress, saveAccount } = data
	let achData = {}

	try {
		if (nonce) {
			achData = {
				saveAccount,
				accountHolder,
				amount: amount,
				nonce,
				bcOrderId,
				deviceData,
				billingAddress,
			}
		} else {
			achData = {
				saveAccount,
				accountHolder,
				token,
				amount: amount,
				bcOrderId,
			}
		}
		const response = await axios.post(`${api}/controllers/braintree/ach`, achData)

		if (response.data.success) console.log('Payment successful! Braintree Transaction ID:', response.data?.data?.id)

		return response.data
	} catch (error) {
		console.log('Error caught from processACHPayment: ', error)
		return { success: false, message: 'Error processing payment', error }
	}
}

async function processSandboxACHPayment(data) {
	const { accountHolder, nonce, token, amount, deviceData, bcOrderId, api, billingAddress, saveAccount } = data
	let achData = {}

	try {
		if (nonce) {
			achData = {
				saveAccount,
				accountHolder,
				amount: amount,
				nonce,
				bcOrderId,
				deviceData,
				billingAddress,
			}
		} else {
			achData = {
				saveAccount,
				accountHolder,
				token,
				amount: amount,
				bcOrderId,
			}
		}
		const response = await axios.post(`${api}/controllers/braintree/sandbox/ach`, data)

		//if (response.data.success) console.log("Payment successful! Braintree Transaction ID:", response.data?.data?.id);

		return response.data
	} catch (error) {
		console.log('Error caught from processSandboxACHPayment: ', error)
		return { success: false, message: 'Error processing payment', error }
	}
}

async function deleteACHPaymentMethod(token, api) {
	console.log('Token: ', token)
	try {
		const response = await axios.delete(`${api}/controllers/braintree/ach/${token}`)
		return response.data
	} catch (error) {
		console.error('Error deleting ACH payment method:', error)
		return { success: false, message: 'Error deleting ACH payment method' }
	}
}

export default {
	getClientToken,
	getSandboxClientToken,
	initiatePayPalCheckout,
	initiateVenmoCheckout,
	initiateACHCheckout,
	processPayment,
	processACHPayment,
	processSandboxACHPayment,
	deleteACHPaymentMethod,
	dataCollector,
}
